import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useAuthUser, useUserPermissions } from 'auth/hooks';
import { AgGridTable } from 'core/components';
import { AccessPermissions } from 'core/enums';
import { AlternateVersionHelper } from 'core/helpers';
import { useAgGridTableContext } from 'core/states';
import { QuotePartAddToQuoteButton } from 'quotes/components/QuotePartAddToQuote';
import { AddLineButton, DownloadButton, RemoveButton } from 'quotes/components/QuotePartList';
import { QuoteEntityType, QuotePartListTriggerAction, QuoteStatus, QuoteType } from 'quotes/enums';
import { VendorQuoteHelper } from 'quotes/helpers';
import { IVendorQuote } from 'quotes/types';

import {
  ImportButton,
  OrderVendorQuoteButton,
  SendAsSpaButton,
  SendToFilenetButton,
  SendToVendorButton,
  SubmitVendorOrderButton,
} from './Actions';

interface IProps {
  quote: IVendorQuote;
  quoteEntityType?: QuoteEntityType;
}

export const QuotePartListTable = ({ quote, quoteEntityType = QuoteEntityType.QUOTE }: IProps) => {
  const { user } = useAuthUser();
  const { hasPermission } = useUserPermissions();

  // The FileNet retry is an alternate-platform action — hidden for classic users.
  const isAlternateVersionEnabled = AlternateVersionHelper.isAlternateVersionEnabled({ user });

  const [trigger, setTrigger] = useState<QuotePartListTriggerAction | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();

  const { deselectAllRows, selectedRows } = useAgGridTableContext();

  useEffect(() => {
    if (!searchParams.has('trigger')) {
      setTrigger(null);

      return;
    }

    setTrigger(searchParams.get('trigger') as QuotePartListTriggerAction);
    setSearchParams([]);
  }, [searchParams, setSearchParams]);

  const hasVendorQuoteEditPermission = hasPermission(
    AccessPermissions.ACCESS_COMPANY_VENDOR_QUOTE_EDIT
  );

  const getActionButtons = () => {
    const actionButtons = [];

    if (VendorQuoteHelper.canAddVendorQuoteParts({ quote, quoteEntityType })) {
      actionButtons.push(
        <AddLineButton key="addLine" quote={quote} quoteType={QuoteType.VENDOR} />
      );
    }

    actionButtons.push(
      <DownloadButton
        key="download"
        quote={quote}
        quoteType={QuoteType.VENDOR}
        quoteEntityType={quoteEntityType}
        trigger={trigger}
      />
    );

    if (QuoteStatus.ORDER_REQUESTED_BY_CUSTOMER !== quote.status) {
      actionButtons.push(
        <ImportButton
          key="import"
          quote={quote}
          quoteEntityType={quoteEntityType}
          importVendorQuoteProps={{
            refetchQueriesKeys: ['vendorQuoteParts'],
            isUpdatingCurrentQuote: true,
            withoutConfirmation: true,
          }}
        />
      );
    }

    if (
      QuoteEntityType.ORDER === quoteEntityType &&
      QuoteStatus.ORDER_DRAFT_CUSTOMER === quote.status
    ) {
      actionButtons.push(<SubmitVendorOrderButton key="submitOrder" quote={quote} />);
    }

    if (QuoteEntityType.QUOTE === quoteEntityType) {
      // Sending an Imported VQ to the vendor flips it to Open, which irreversibly destroys its
      // imported-only capabilities (edit rights, Attributes, Send as SPA eligibility).
      if (!VendorQuoteHelper.isImportedVendorQuote({ quote, quoteEntityType })) {
        actionButtons.push(<SendToVendorButton key="sendToVendor" quote={quote} />);
      }

      actionButtons.push(<OrderVendorQuoteButton key="order" quote={quote} />);
      actionButtons.push(<SendAsSpaButton key="sendAsSpa" quote={quote} />);

      if (isAlternateVersionEnabled && QuoteStatus.EDITABLE_BY_CUSTOMER === quote.status) {
        actionButtons.push(<SendToFilenetButton key="sendToFilenet" quote={quote} />);
      }
    }

    return actionButtons;
  };

  const getSelectionActionButtons = () => {
    const selectionActionButtons = [
      <QuotePartAddToQuoteButton
        key="addToQuote"
        quote={quote}
        quoteParts={selectedRows}
        onSubmitSuccess={deselectAllRows}
      />,
      <QuotePartAddToQuoteButton
        key="addToOrder"
        quote={quote}
        quoteParts={selectedRows}
        onSubmitSuccess={deselectAllRows}
        entityType={QuoteEntityType.ORDER}
      />,
    ];

    if (
      VendorQuoteHelper.canRemoveVendorQuoteParts({
        quote,
        quoteEntityType,
        hasVendorQuoteEditPermission,
      })
    ) {
      selectionActionButtons.push(<RemoveButton key="remove" quote={quote} />);
    }

    return selectionActionButtons;
  };

  return (
    <AgGridTable
      actionButtons={getActionButtons()}
      selectionActionButtons={getSelectionActionButtons()}
      className="statusBarLeftFullWidth"
    />
  );
};
